/*
        classCreated: (namespace, classConstructor) =>
        {
            classConstructor.createForInterfaceDesigner = () =>
            {
                const
                    table = new classConstructor();

                table.addColumn(new TableColumn());
                table.addRow(table.newRow());

                return table;
            };
        }
*/
import { Assert } from "../../Assert";
import { AsyncIterator } from "../../AsyncIterator";
import { DataSource } from "../../Data/DataSource";
import { DataSourcePropertyValueChange } from "../../Data/DataSourcePropertyValueChange";
import { Constructor, Nullable, Nullish, PartialNullable } from "../../Extensions";
import { ArrayHelper } from "../../Helpers/ArrayHelper";
import { DateHelper } from "../../Helpers/DateHelper";
import { EventHelper } from "../../Helpers/EventHelper";
import { NumberHelper } from "../../Helpers/NumberHelper";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { $$, IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyInt32, PropertyObject, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { ElementRef } from "../ElementRef";
import { JsxFragment } from "../JsxFragment";
import { Point } from "../Point";
import { CheckBox } from "./CheckBox";
import { ContainerControl, ContainerControlPropertyValues, IContainerControl } from "./ContainerControl";
import { Control, ControlPropertyValues, IControl } from "./Control";
import { ControlRef } from "./ControlRef";
import type { ITableColumn } from "./TableColumn";
import { NAMESPACE } from "./_Namespace";

import "./Table.scss";

// #region TableColumnBase

export type TableColumnBasePropertyValues =
    ContainerControlPropertyValues
    & {
        table: Table;
        tableCellClass: Constructor<TableCellBase>;
    };

export type TableColumnBaseProperties = {
    [name in keyof TableColumnBasePropertyValues]: Property<TableColumnBasePropertyValues[name]>
};

export interface ITableColumnBase extends IContainerControl, TableColumnBaseProperties
{
}

export abstract class TableColumnBase extends ContainerControl implements ITableColumnBase
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableColumnBase";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableColumnBasePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Properties

    public table!: Property<Table>;
    public tableCellClass!: Property<Constructor<TableCellBase>>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableColumnBasePropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-column-base");
    }

    // #endregion

    // #region Protected Methods

    protected _tableCellClassChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _tableChanged(change: PropertyChange<Table>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TableColumnBasePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.table = new Property<Table>(this, "System.Object, System.Private.CoreLib", propertyValues.table, this._tableChanged);
        this.tableCellClass = new Property<Constructor<TableCellBase>>(this, "System.Object, System.Private.CoreLib", propertyValues.tableCellClass, this._tableCellClassChanged);
    }

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <th ref={this._containerElementRef}>
                {children ?? this._createChildrenHtml()}
            </th>
        );
    }

    // #endregion

    // #endregion
}

// #endregion

// #region TableColumnIcon

export type TableColumnIconPropertyValues =
    TableColumnBasePropertyValues
    & {
    };

export type TableColumnIconProperties = {
    [name in keyof TableColumnIconPropertyValues]: Property<TableColumnIconPropertyValues[name]>
};

export interface ITableColumnIcon extends ITableColumnBase, TableColumnIconProperties
{
}

export class TableColumnIcon extends TableColumnBase implements ITableColumnIcon
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableColumnIcon";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableColumnIconPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                tableCellClass: TableCellIcon,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableColumnIconPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-column-icon");
    }

    // #endregion

    // #endregion
}

// #endregion

// #region TableColumnSelection

export type TableColumnSelectionPropertyValues =
    TableColumnBasePropertyValues
    & {
        rowSelectType: string;
    };

export type TableColumnSelectionProperties = {
    [name in keyof TableColumnSelectionPropertyValues]: Property<TableColumnSelectionPropertyValues[name]>
};

export interface ITableColumnSelection extends ITableColumnBase, TableColumnSelectionProperties
{
}

export class TableColumnSelection extends TableColumnBase implements ITableColumnSelection
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableColumnSelection";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableColumnSelectionPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                rowSelectType: TableRowSelectTypeEnum.single,
                tableCellClass: TableCellSelection,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    public checkBox?: CheckBox;

    // #endregion

    // #region Properties

    public rowSelectType!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableColumnSelectionPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-column-selection");

        // Trigger Property Changes
        this.rowSelectType.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _rowSelectTypeChanged(change: PropertyChange<string>)
    {
        this.checkBox?.visible.set(change.currentValue === TableRowSelectTypeEnum.multiple);
    }
    protected _setRowsSelectedState(selected: boolean)
    {
        const
            table = this.table.get();

        if (!table)
            return;

        table._selectedItemsUpdateSuspend();

        if (table.lightweight.get())
        {
            table.$$element.get()?.find(".rad-control-table-selection-cell .rad-control-check-box")
                .each((index, element) =>
                {
                    const
                        $$checkBox = $$(element),
                        checkBox = $$checkBox.radControl();

                    if (checkBox)
                    {
                        const
                            $$row = $$checkBox.closest("tr") as JNative<HTMLTableRowElement>;

                        checkBox.value.set(selected);
                        table._setRowSelectionState($$row, selected);
                    }
                });
        }
        else
        {
            (table.rowsGet() as TableRow[])
                .forEach(row =>
                {
                    const
                        tableCellSelection = ArrayHelper.firstOrDefault(row.controlsGet(), item => item instanceof TableCellSelection) as TableCellSelection | undefined;

                    tableCellSelection?.checkBox?.value.set(selected);
                });
        }

        table._selectedItemsUpdateResume();
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TableColumnSelectionPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.rowSelectType = new PropertyString(this, propertyValues.rowSelectType, this._rowSelectTypeChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.checkBox, "this.checkBox");

        this.checkBox.on("valueChanged", { self: this }, this.onCheckBoxValueChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.checkBox =
            new CheckBox()
                .appendTo(this);

        return $$element;
    }

    // #endregion

    // #region Event Handlers

    protected onCheckBoxValueChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        self._setRowsSelectedState(!!change.currentValue);
    }

    // #endregion

    // #endregion
}

// #endregion

// #region TableCellBase

export type TableCellCreateOptions = {
    table: ITable;
    tableColumn: ITableColumn;
    tableRow: ITableRow;
};

export type TableCellBasePropertyValues =
    ContainerControlPropertyValues
    & {
        columnDescription: string;
        table: ITable;
        tableColumn: ITableColumnBase;
        tableRow: ITableRow;
    };

export type TableCellBaseProperties = {
    [name in keyof TableCellBasePropertyValues]: Property<TableCellBasePropertyValues[name]>
};

export interface ITableCellBase extends IContainerControl, TableCellBaseProperties
{
}

export abstract class TableCellBase extends ContainerControl<TableCellBasePropertyValues> implements ITableCellBase
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableCellBase";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableCellBasePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _columnDescriptionElementRef!: ElementRef;
    protected _contentsElementRef!: ElementRef;
    protected _textElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public columnDescription!: PropertyString;
    public table!: Property<ITable>;
    public tableColumn!: Property<ITableColumnBase>;
    public tableRow!: Property<ITableRow>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableCellBasePropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-cell-base");
        this._columnDescriptionElementRef.current.addClass("column-description");
    }

    // #endregion

    // #region Protected Methods

    protected _columnDescriptionChanged(change: PropertyChange<string>)
    {
        this._columnDescriptionElementRef.current.text(change.currentValue);
    }
    protected _tableChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _tableColumnChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _tableRowChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TableCellBasePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._columnDescriptionElementRef = new ElementRef();
        this._contentsElementRef = new ElementRef();
        this._textElementRef = new ElementRef();

        this.columnDescription = new PropertyString(this, propertyValues.columnDescription, this._columnDescriptionChanged);
        this.table = new Property<ITable>(this, "System.Object, System.Private.CoreLib", propertyValues.table, this._tableChanged);
        this.tableColumn = new Property<ITableColumnBase>(this, "System.Object, System.Private.CoreLib", propertyValues.tableColumn, this._tableColumnChanged);
        this.tableRow = new Property<ITableRow>(this, "System.Object, System.Private.CoreLib", propertyValues.tableRow, this._tableRowChanged);
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        const
            text = $$element.text();

        if (text?.replace(/[ \r\n]/g, StringHelper.empty).length)
            this.text.set(text, false)

        return $$element;
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <td>
                <span ref={this._columnDescriptionElementRef}></span>
                <div ref={this._contentsElementRef} class="contents">
                    <span ref={this._textElementRef} class="text"></span>
                </div>
                <div ref={this._containerElementRef}>
                    {children ?? this._createChildrenHtml()}
                </div>
            </td>
        );
    }
    protected override _textChanged(change: PropertyChange<string>)
    {
        super._textChanged(change);

        const
            lines = change.currentValue ? change.currentValue.replace(/\r/gi, StringHelper.empty).split("\n") : null;

        this._textElementRef.current.empty();

        if (!lines)
            return;

        lines.forEach((item, index) =>
        {
            this._textElementRef.current.append(JNativeStatic.createText(item));

            if (index < lines.length - 1)
                this._textElementRef.current.append(JNativeStatic.create$$({ nodeName: "br" }));
        });
    }

    protected override onMouseUp(evt: IJNativeEvent)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #endregion
}

// #endregion

// #region TableCell

export enum TableCellBooleanTypeEnum
{
    none = "none",
    yesNo = "yesNo",
    tickCross = "tickCross",
    tickNoCross = "tickNoCross",
}

export type TableCellPropertyValues =
    TableCellBasePropertyValues
    & {
        booleanStyle: string;
        columnSpan: number;
        dataSourceProperty: string;
        formatString: string;
    };

export type TableCellProperties = {
    [name in keyof TableCellPropertyValues]: Property<TableCellPropertyValues[name]>
};

export interface ITableCell extends ITableCellBase, TableCellProperties
{
}

export class TableCell extends TableCellBase implements ITableCell
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableCell";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableCellPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                booleanStyle: TableCellBooleanTypeEnum.yesNo,
                columnSpan: 1,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Varibles

    protected $$elementForDisplay?: JNative<HTMLElement>;

    // #endregion

    // #region Properties

    public booleanStyle!: PropertyString;
    public columnSpan!: PropertyInt32;
    public dataSourceProperty!: PropertyString;
    public formatString!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableCellPropertyValues>)
    {
        propertyValues ??= {};

        const
            tableCellEntitySearchCreateOptions: TableCellCreateOptions = {
                table: propertyValues.table as Table,
                tableColumn: propertyValues.tableColumn as ITableColumn,
                tableRow: propertyValues.tableRow as TableRow,
            };

        if (tableCellEntitySearchCreateOptions.tableColumn)
        {
            propertyValues.dataSourceProperty = tableCellEntitySearchCreateOptions.tableColumn?.dataSourceProperty.get();
            propertyValues.formatString = tableCellEntitySearchCreateOptions.tableColumn.formatString.get();
        }

        super(propertyValues);

        this.addCssClass("rad-control-table-cell");

        // Trigger Property Changes
        this.columnSpan.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _booleanStyleChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _columnSpanChanged(change: PropertyChange<number>)
    {
        const
            $$element = this.$$element.get();

        if (!$$element)
            return;

        const
            columnSpan = change.currentValue ? change.currentValue : 1;

        ($$element as JNative<HTMLTableCellElement>).prop("colSpan", columnSpan === 1 ? StringHelper.empty : columnSpan);
    }
    protected _dataSourcePropertyChanged(change: PropertyChange<string>)
    {
        this.refreshFromDataSource();
    }
    protected _formatStringChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _getDataSourceProperty()
    {
        return this.dataSourceProperty.get();
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TableCellPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.booleanStyle = new PropertyString(this, propertyValues.booleanStyle, this._booleanStyleChanged);
        this.columnSpan = new PropertyInt32(this, propertyValues.columnSpan, this._columnSpanChanged);
        this.dataSourceProperty = new PropertyString({
            context: this,
            initialValue: propertyValues.dataSourceProperty,
            changeHandler: this._dataSourcePropertyChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Data Source Property" }),
            ],
        });
        this.formatString = new PropertyString(this, propertyValues.formatString, this._formatStringChanged);
    }

    protected override _refreshFromDataSource()
    {
        super._refreshFromDataSource();

        const
            dataSource = this.dataSource.get(),
            dataSourceProperty = this._getDataSourceProperty(),
            dataDateFormatString = "yyyy-MM-dd",
            dataDateTimeFormatString = "yyyy-MM-ddTHH:mm:ss.fffffffZ",
            formatString = this.formatString.get(),
            table = this.table.get();

        if (this.$$elementForDisplay)
        {
            this.$$elementForDisplay.remove();
            delete this.$$elementForDisplay;
        }

        if (!dataSource || !dataSourceProperty)
        {
            this.text.set(StringHelper.empty);
            return;
        }

        const
            dataSourcePropertyValue = dataSource.propertyValueByPathGet<any>(dataSourceProperty),
            dataSourcePropertyValueType = ObjectHelper.typeOf(dataSourcePropertyValue),
            urlProtocolsToLink = [
                "http://",
                "https://"
            ];

        let
            stringForDisplay = StringHelper.empty;

        if (dataSourcePropertyValue !== undefined && dataSourcePropertyValue !== null)
        {
            switch (dataSourcePropertyValueType)
            {
                case "boolean":
                    {
                        switch (this.booleanStyle.get())
                        {
                            case TableCellBooleanTypeEnum.yesNo:
                                stringForDisplay = dataSourcePropertyValue ? "Yes" : "No";
                                break;
                            case TableCellBooleanTypeEnum.tickCross:
                                this.$$elementForDisplay =
                                    JNativeStatic.create$$({
                                        nodeName: "i",
                                        attributes: { "class": dataSourcePropertyValue ? "check icon" : "times icon" }
                                    });

                                break;
                            case TableCellBooleanTypeEnum.tickNoCross:
                                this.$$elementForDisplay =
                                    JNativeStatic.create$$({
                                        nodeName: "i",
                                        attributes: { "class": dataSourcePropertyValue ? "check icon" : StringHelper.empty }
                                    });
                                break;
                            default:
                                stringForDisplay = dataSourcePropertyValue ? "True" : "False";
                                break;
                        }
                        break;
                    }
                case "date":
                    {
                        stringForDisplay = dataSourcePropertyValue.toString(formatString ? formatString : "dd/MM/yyyy HH:mm:ss");
                        break;
                    }
                case "number":
                    {
                        stringForDisplay = NumberHelper.toStringFormat(dataSourcePropertyValue, formatString ?? undefined);
                        break;
                    }
                case "string":
                    {
                        if (typeof dataSourcePropertyValue == "string" && StringHelper.isDate(dataSourcePropertyValue, dataDateTimeFormatString))
                            // Date Time string
                            stringForDisplay = DateHelper.toString(DateHelper.fromString(dataSourcePropertyValue, dataDateTimeFormatString), formatString ? formatString : "dd/MM/yyyy HH:mm:ss");
                        else if (typeof dataSourcePropertyValue == "string" && StringHelper.isDate(dataSourcePropertyValue, dataDateFormatString))
                            // Date string
                            stringForDisplay = DateHelper.toString(DateHelper.fromString(dataSourcePropertyValue, dataDateFormatString), formatString ? formatString : "dd/MM/yyyy");
                        else if (table?.linkUrls.get() && urlProtocolsToLink.some(item => dataSourcePropertyValue.startsWith(item, { ignoreCase: true })))
                        {
                            this.$$elementForDisplay = JNativeStatic.create$$({ nodeName: "a", attributes: { target: "_system" } });
                            this.$$elementForDisplay.attr("href", dataSourcePropertyValue);
                            this.$$elementForDisplay.text(dataSourcePropertyValue);
                            this.$$elementForDisplay.on("click mousedown mouseup", (evt: IJNativeEvent) =>
                            {
                                evt.stopPropagation();
                            });
                        }
                        else
                            stringForDisplay = dataSourcePropertyValue.toString();
                        break;
                    }
                default:
                    {
                        stringForDisplay = dataSourcePropertyValue.toString();
                        break;
                    }
            }
        }

        if (this.$$elementForDisplay)
        {
            this.text.set(null);
            this._contentsElementRef.current.append(this.$$elementForDisplay);
        }
        else
            this.text.set(stringForDisplay);
    }

    protected override onDataSourcePropertyValueChanged(evt: IJNativeEvent, change: DataSourcePropertyValueChange<any>)
    {
        const
            self = evt.data.self as this;

        if (change.path !== self.dataSourceProperty.get())
            return;

        self.refreshFromDataSource();
    }

    // #endregion

    // #endregion
}

// #endregion

// #region TableCellIcon

export type TableCellIconPropertyValues =
    TableCellBasePropertyValues
    & {
        icon: string;
        iconDataSourcePath: string;
        iconMobile: string;
    };

export type TableCellIconProperties = {
    [name in keyof TableCellIconPropertyValues]: Property<TableCellIconPropertyValues[name]>
};

export interface ITableCellIcon extends ITableCellBase, TableCellIconProperties
{
}

export class TableCellIcon extends TableCellBase implements ITableCellIcon
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableCellIcon";

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _iconElementRef!: ElementRef;
    protected _iconMobileElementRef!: ElementRef;

    // #endregion

    // #region Properties

    public icon!: PropertyString;
    public iconDataSourcePath!: PropertyString;
    public iconMobile!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableCellIconPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-icon-cell");

        // Trigger Property Changes
        this.icon.triggerChange();
        this.iconMobile.triggerChange();
    }

    // #endregion

    // #region Protected Methods

    protected _iconChanged(change: PropertyChange<string>)
    {
        if (change.originalValue)
            this._iconElementRef.current.removeClass(change.originalValue);

        if (change.currentValue)
            this._iconElementRef.current.addClass(change.currentValue);
    }
    protected _iconDataSourcePathChanged(change: PropertyChange<string>)
    {
        if (change.originalValue)
            this.icon.set(null);

        this._updateIconFromDataSource();
    }
    protected _iconMobileChanged(change: PropertyChange<string>)
    {
        if (change.originalValue)
            this._iconMobileElementRef.current.removeClass(change.originalValue);

        if (change.currentValue)
            this._iconMobileElementRef.current.addClass(change.currentValue);
    }
    protected _updateIconFromDataSource()
    {
        const
            dataSource = this.dataSource.get();

        if (!dataSource)
            return;

        const
            iconDataSourcePath = this.iconDataSourcePath.get();

        if (iconDataSourcePath)
        {
            const
                iconValue = dataSource.propertyValueByPathGet(iconDataSourcePath);

            this.icon.set(iconValue ? StringHelper.format("rad-icon-{0}-16", iconValue) : null);
            this.iconMobile.set(iconValue ? StringHelper.format("rad-icon-{0}-48", iconValue) : null);
        }
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TableCellIconPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._iconElementRef = new ElementRef();
        this._iconMobileElementRef = new ElementRef();

        this.icon = new PropertyString(this, propertyValues.icon, this._iconChanged);
        this.iconDataSourcePath = new PropertyString(this, propertyValues.iconDataSourcePath, this._iconDataSourcePathChanged);
        this.iconMobile = new PropertyString(this, propertyValues.iconMobile, this._iconMobileChanged);
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this._contentsElementRef.current.hide();

        return $$element;
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <td>
                <span ref={this._columnDescriptionElementRef}></span>
                <div ref={this._contentsElementRef} class="contents">
                    <span ref={this._textElementRef} class="text"></span>
                </div>
                <div ref={this._containerElementRef}>
                    <i ref={this._iconElementRef} class="rad-device-desktop">
                        <span></span>
                    </i>
                    <i ref={this._iconMobileElementRef} class="rad-device-mobile">
                        <span></span>
                    </i>
                    {children ?? this._createChildrenHtml()}
                </div>
            </td>
        );
    }
    protected override _dataSourceChanged(change: PropertyChange<DataSource>)
    {
        super._dataSourceChanged(change);
        this._updateIconFromDataSource();
    }

    protected override onDataSourcePropertyValueChanged(evt: IJNativeEvent, change: DataSourcePropertyValueChange<any>)
    {
        const
            self = evt.data.self as this;

        self._updateIconFromDataSource();
    }

    // #endregion

    // #endregion
}

// #endregion

// #region TableCellSelection

export type TableCellSelectionPropertyValues =
    TableCellBasePropertyValues
    & {
        selectionIcon: string;
        selectionIconMobile: string;
        selectionIconMobileSelected: string;
        selectionIconSelected: string;
    };

export type TableCellSelectionProperties = {
    [name in keyof TableCellSelectionPropertyValues]: Property<TableCellSelectionPropertyValues[name]>
};

export interface ITableCellSelection extends ITableCellBase, TableCellSelectionProperties
{
}

export class TableCellSelection extends TableCellBase implements ITableCellSelection
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableCellSelection";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableCellSelectionPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                selectionIcon: "square outline icon",
                selectionIconMobile: "square outline icon",
                selectionIconMobileSelected: "checkmark box icon",
                selectionIconSelected: "checkmark box icon",
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _checkBoxControlRef!: ControlRef<CheckBox>;
    public checkBox?: CheckBox;

    // #endregion

    // #region Properties

    public selectionIcon!: PropertyString;
    public selectionIconMobile!: PropertyString;
    public selectionIconMobileSelected!: PropertyString;
    public selectionIconSelected!: PropertyString;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableCellSelectionPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-selection-cell");
    }

    // #endregion

    // #region Public Methods

    public deselect()
    {
        Assert.isNotNullOrUndefined(this.checkBox, "this.checkBox");
        this.checkBox.value.set(false);
    }
    public select()
    {
        Assert.isNotNullOrUndefined(this.checkBox, "this.checkBox");
        this.checkBox.value.set(true);
    }

    // #endregion

    // #region Protected Methods

    protected _selectionIconChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _selectionIconMobileChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _selectionIconMobileSelectedChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _selectionIconSelectedChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TableCellSelectionPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._checkBoxControlRef = new ControlRef();

        this.selectionIcon = new PropertyString(this, propertyValues.selectionIcon, this._selectionIconChanged);
        this.selectionIconMobile = new PropertyString(this, propertyValues.selectionIconMobile, this._selectionIconMobileChanged);
        this.selectionIconMobileSelected = new PropertyString(this, propertyValues.selectionIconMobileSelected, this._selectionIconMobileSelectedChanged);
        this.selectionIconSelected = new PropertyString(this, propertyValues.selectionIconSelected, this._selectionIconSelectedChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();

        Assert.isNotNullOrUndefined(this.checkBox, "this.checkBox");
        this.checkBox.on("valueChanged", { self: this }, this.onCheckBoxValueChanged);
    }
    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this._contentsElementRef.current.hide();
        this.checkBox = this._checkBoxControlRef.current;

        return $$element;
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <td>
                <span ref={this._columnDescriptionElementRef}></span>
                <div ref={this._contentsElementRef} class="contents">
                    <span ref={this._textElementRef} class="text"></span>
                </div>
                <div ref={this._containerElementRef}>
                    <CheckBox ref={this._checkBoxControlRef}></CheckBox>
                    {children ?? this._createChildrenHtml()}
                </div>
            </td>
        );
    }

    // #endregion

    // #region Event Handlers

    protected onCheckBoxValueChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this,
            $$element = self.$$element.get();

        Assert.isNotNullOrUndefined($$element, "$$element");

        const
            $$tableRow = $$element.closest(".rad-control-table-row"),
            $$table = $$tableRow.closest(".rad-control-table"),
            tableRow = $$tableRow.radControl(),
            table = $$table.radControl();

        if (table && tableRow)
            table._setRowSelectionState(tableRow, change.currentValue);
    }

    // #endregion

    // #endregion
}

// #endregion

// #region TableRow

export type TableRowPropertyValues =
    ContainerControlPropertyValues
    & {
        table: Table;
    };

export type TableRowProperties = {
    [name in keyof TableRowPropertyValues]: Property<TableRowPropertyValues[name]>
};

export interface ITableRow extends IContainerControl, TableRowProperties
{
}

type TChildControlRow = TableCellBase;

export class TableRow<TPropertyValues extends TableRowPropertyValues = TableRowPropertyValues> extends ContainerControl<TPropertyValues, TChildControlRow> implements ITableRow
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableRow";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableRowPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
            }
        };
    }
    public static override getAllowedChildControlClasses()
    {
        return [TableCellBase];
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected startPoint?: Point;

    // #endregion

    // #region Properties

    public table!: Property<Table>;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-row");
    }

    // #endregion

    // #region Public Methods

    public cellsGet()
    {
        return this.controlsGet();
    }
    public click()
    {
        const
            table = this._getTable();

        if (table)
            table._rowClicked(this);
    }
    public deselect()
    {
        const
            selectionCell = ArrayHelper.firstOrDefault(this.cellsGet(), item => item instanceof TableCellSelection) as Nullable<TableCellSelection>;

        if (selectionCell)
            selectionCell.deselect();

        this.removeCssClass("rad-state-selected");
    }
    public select()
    {
        const
            selectionCell = ArrayHelper.firstOrDefault(this.cellsGet(), item => item instanceof TableCellSelection) as Nullable<TableCellSelection>;

        if (selectionCell)
            selectionCell.select();

        this.addCssClass("rad-state-selected");
    }
    public toggle()
    {
        if (this._selectedGet())
            this.deselect();
        else
            this.select();
    }

    // #endregion

    // #region Protected Methods

    protected _getTable()
    {
        return this.$$element.get()?.closest(".rad-control-table").radControl() as Table | undefined;
    }
    public _selectedGet()
    {
        return this.hasCssClass("rad-state-selected");
    }
    protected _tableChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.table = new Property<Table>(this, "System.Object, System.Private.CoreLib", propertyValues.table, this._tableChanged);
    }

    protected override _bindEvents()
    {
        super._bindEvents();
        this.on("mousedown", { self: this }, this.onMouseDown);
        this.on("mouseup", { self: this }, this.onMouseUp);
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <tr ref={this._containerElementRef}>
                {children ?? this._createChildrenHtml()}
            </tr>
        );
    }
    protected override _refreshFromDataSource()
    {
        super._refreshFromDataSource();

        const
            dataSource = this.dataSource.get();

        if (!dataSource)
            return;

        for (let cell of this.cellsGet())
            cell.dataSource.set(dataSource);
    }
    protected override _suspendLayoutChanged(change: PropertyChange<boolean>)
    {
        super._suspendLayoutChanged(change);

        if (!change.currentValue)
        {
            const
                dataSource = this.dataSource.get();

            this.dataSource.set(null);
            this.dataSource.set(dataSource);
        }
    }

    public override append($$elementOrControlOrArrayOf: JNative | TChildControlRow | TChildControlRow[], beforeSelector?: any)
    {
        super.append($$elementOrControlOrArrayOf, beforeSelector);

        if (!this.suspendLayout.get())
            ArrayHelper.last(this.controlsGet()).dataSource.set(this.dataSource.get());

        return this;
    }
    public override prepend($$elementOrControlOrArrayOf: JNative | TChildControlRow | TChildControlRow[], beforeSelector?: any)
    {
        super.prepend($$elementOrControlOrArrayOf, beforeSelector);

        if (!this.suspendLayout.get())
            ArrayHelper.first(this.controlsGet()).dataSource.set(this.dataSource.get());

        return this;
    }
    public override remove()
    {
        const
            table = this._getTable();

        if (table)
            table._setRowSelectionState(this, false);

        const
            rowDataSource = this.dataSource.get(),
            rowData = rowDataSource ? rowDataSource.data.get() : null;

        if (rowData)
        {
            const
                tableDataSource = table ? table.dataSource.get() : null,
                tableData = tableDataSource ? tableDataSource.data.get() : null;

            if (tableData)
                ArrayHelper.remove(tableData, rowData);
        }

        return super.remove();
    }

    protected override onDataSourcePropertyValueChanged(evt: IJNativeEvent, change: DataSourcePropertyValueChange<any>)
    {
        const
            self = evt.data.self as this;

        super.onDataSourcePropertyValueChanged(evt, change);
        self.table.get()?._triggerRowDataSourceChanged(self, change);
    }
    protected override onMouseUp(evt: IJNativeEvent)
    {
        super.onMouseUp(evt);

        const
            self = evt.data.self as this;

        if (!self.startPoint)
            return;

        const
            startPoint = self.startPoint,
            endPoint = EventHelper.getInputCoordinates(evt);

        delete self.startPoint;

        if (!endPoint)
            return;

        if (startPoint.distanceTo(endPoint) > 10)
            return;

        self.click();
    }

    // #endregion

    // #region Event Handlers

    protected onMouseDown(evt: IJNativeEvent<MouseEvent>)
    {
        const
            self = evt.data.self as this;

        if (evt.originalEvent.button === 0)
            self.startPoint = EventHelper.getInputCoordinates(evt);
    }

    // #endregion

    // #endregion
}

// #endregion

// #region TableRowNoResults

export type TableRowNoResultsPropertyValues =
    TableRowPropertyValues
    & {
    };

export type TableRowNoResultsProperties = {
    [name in keyof TableRowNoResultsPropertyValues]: Property<TableRowNoResultsPropertyValues[name]>
};

export interface ITableRowNoResults extends ITableRow, TableRowNoResultsProperties
{
}

export class TableRowNoResults extends TableRow implements ITableRowNoResults
{
    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableRowNoResults";

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableRowNoResultsPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-row-no-results");
    }

    // #endregion
}

// #endregion

// #region TableHead

export type TableHeadPropertyValues =
    ContainerControlPropertyValues
    & {
    };

export type TableHeadProperties = {
    [name in keyof TableHeadPropertyValues]: Property<TableHeadPropertyValues[name]>
};

export interface ITableHead extends IContainerControl, TableHeadProperties
{
}

type TChildControlHead = TableColumnBase;

export class TableHead<TPropertyValues extends TableHeadPropertyValues = TableHeadPropertyValues> extends ContainerControl<TPropertyValues, TChildControlHead> implements ITableHead
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableHead";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableHeadPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
            }
        };
    }
    public static override getAllowedChildControlClasses()
    {
        return [TableColumnBase];
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-head");
    }

    // #endregion

    // #region Overrides

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <thead>
                <tr ref={this._containerElementRef}>
                </tr>
            </thead>
        );
    }

    // #endregion

    // #endregion
}

// #endregion

// #region TableBody

export type TableBodyPropertyValues =
    ContainerControlPropertyValues
    & {
    };

export type TableBodyProperties = {
    [name in keyof TableBodyPropertyValues]: Property<TableBodyPropertyValues[name]>
};

export interface ITableBody extends IContainerControl, TableBodyProperties
{
}

export class TableBody extends ContainerControl implements ITableBody
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableBody";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableBodyPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableBodyPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-body");
    }

    // #endregion

    // #region Overrides

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <tbody ref={this._containerElementRef}>
                {children ?? this._createChildrenHtml()}
            </tbody>
        );
    }

    // #endregion

    // #endregion
}

// #endregion

// #region TableFoot

export type TableFootPropertyValues =
    ContainerControlPropertyValues
    & {
    };

export type TableFootProperties = {
    [name in keyof TableFootPropertyValues]: Property<TableFootPropertyValues[name]>
};

export interface ITableFoot extends IContainerControl, TableFootProperties
{
}

export class TableFoot extends ContainerControl implements ITableFoot
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "TableFoot";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TableFootPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
            }
        };
    }
    public static override getAllowedChildControlClasses()
    {
        return [Control];
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TableFootPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-table-foot");
    }

    // #endregion

    // #region Overrides

    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <tfoot>
                <tr>
                    <td ref={this._containerElementRef}></td>
                </tr>
            </tfoot>
        );
    }

    // #endregion

    // #endregion
}

// #endregion

// #region Table

export enum TableRowSelectTypeEnum
{
    none = "none",
    single = "single",
    singleNoCheckBox = "singleNoCheckBox",
    multiple = "multiple",
}

export type TablePropertyValues =
    ControlPropertyValues
    & {
        asyncRefreshFromDataSource: boolean;
        footerVisible: boolean;
        headerVisible: boolean;
        ledger: boolean;
        lightweight: boolean;
        linkUrls: boolean;
        messageWhenNoRows: string;
        rowIcon: string;
        rowIconDataSourcePath: string;
        rowIconMobile: string;
        rowClickable: boolean;
        rowSelectType: TableRowSelectTypeEnum;
        selectedItems: any[];
        stackable: boolean;
        tableRowClass: any;
    };

export type TableProperties = {
    [name in keyof TablePropertyValues]: Property<TablePropertyValues[name]>
};

export interface ITable extends IControl, TableProperties
{
}

export class Table extends Control implements ITable
{
    // #region Static

    public static override classNamespace = NAMESPACE + "Table";
    public static override className = "Table";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<TablePropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                asyncRefreshFromDataSource: true,
                footerVisible: false,
                headerVisible: true,
                ledger: false,
                lightweight: false,
                rowClickable: false,
                rowSelectType: TableRowSelectTypeEnum.none,
                selectedItems: [],
                stackable: true,
                tableRowClass: TableRow,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected _tableElementRef!: ElementRef;
    protected __selectedItemsUpdateSuspended?: boolean;
    protected __settingRowSelectionState?: boolean;
    protected asyncIterator?: AsyncIterator;
    protected iconColumn?: TableColumnIcon;
    protected selectionColumn?: TableColumnSelection;
    protected _tableBodyControlRef!: ControlRef<TableBody>;
    protected tableBody?: TableBody;
    protected _tableFootControlRef!: ControlRef<TableFoot>;
    public tableFoot?: TableFoot;
    protected _tableHeadControlRef!: ControlRef<TableHead>;
    public tableHead?: TableHead;
    private _tableRowNoResults?: TableRowNoResults;

    // #endregion

    // #region Properties

    public asyncRefreshFromDataSource!: PropertyBoolean;
    public footerVisible!: PropertyBoolean;
    public headerVisible!: PropertyBoolean;
    public ledger!: PropertyBoolean;
    public lightweight!: PropertyBoolean;
    public linkUrls!: PropertyBoolean;
    public messageWhenNoRows!: PropertyString;
    public rowIcon!: PropertyString;
    public rowIconDataSourcePath!: PropertyString;
    public rowIconMobile!: PropertyString;
    public rowClickable!: PropertyBoolean;
    public rowSelectType!: Property<TableRowSelectTypeEnum>;
    public selectedItems!: Property<JNative<HTMLTableRowElement> | TableRow[]>;
    public stackable!: PropertyBoolean;
    public tableRowClass!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<TablePropertyValues>)
    {
        super(propertyValues);

        this.__selectedItemsUpdateSuspended = false;
        this.__settingRowSelectionState = false;

        this.addCssClass("rad-control-table");

        this._updateCellColumnDescriptions();
        //this._updateTableHead();

        // Trigger Property Changes
        this.headerVisible.triggerChange();
        this.ledger.triggerChange();
        this.linkUrls.triggerChange();
        this.rowIcon.triggerChange();
        this.rowIconMobile.triggerChange();
        this.rowClickable.triggerChange();
        this.rowSelectType.triggerChange();
        this.footerVisible.triggerChange();
        this.stackable.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public addColumn(tableColumn: TableColumnBase)
    {
        this.tableHead?.append(tableColumn);
        tableColumn.table.set(this);
        (this.tableFoot?._containerElementRef.current as Nullish<JNative<HTMLTableCellElement>>)?.prop("colSpan", this.tableHead?.controlsGet().length);
    }
    public addRow(tableRow: HTMLTableRowElement | TableRow, prepend?: boolean)
    {
        return this.addRows([tableRow], prepend);
    }
    public addRows(tableRows: (HTMLTableRowElement | TableRow)[], prepend?: boolean)
    {
        if (this.lightweight.get())
        {
            if (prepend)
                this.tableBody?.prepend($$(tableRows));
            else
                this.tableBody?.append($$(tableRows));

            return;
        }

        const
            columns = this.columnsGet();

        (tableRows as TableRow[])
            .forEach(item =>
            {
                const
                    cells = item.controlsGet() as TableCellBase[];

                for (let i = 0; i < columns.length; i++)
                {
                    if (cells.length <= i)
                        break;

                    cells[i].columnDescription.set(columns[i].text.get());
                }
            });

        if (prepend)
            this.tableBody?.prepend(tableRows as TableRow[]);
        else
            this.tableBody?.append(tableRows as TableRow[]);

        //this._updateTableHead();
    }
    public clear()
    {
        this._updateSelectedItems([]);
        this._noResultsRowRemove();

        if (this.tableBody)
        {
            for (let control of this.tableBody.controlsGet())
                control.remove();
        }

        //this._updateTableHead();
    }
    public columnsClear()
    {
        this.clear();

        if (this.iconColumn)
            this.iconColumn.detach();

        if (this.selectionColumn)
            this.selectionColumn.detach();

        this.tableHead?.controlsClear();

        this._updateColumns();
    }
    public columnsGet(): TableColumnBase[]
    {
        return this.tableHead?.controlsGet() ?? [];
    }
    public newRow(properties?: any): TableRow | JNative<HTMLTableRowElement>
    {
        if (this.lightweight.get())
        {
            const
                $$trElement = JNativeStatic.create$$({ nodeName: "tr" }) as JNative<HTMLTableRowElement>;

            let
                columnCount = this.columnsGet().length;

            while (columnCount--)
                $$trElement.append(document.createElement("td"));

            return $$trElement;
        }

        const
            tableRowClass = this.tableRowClass.get();

        properties = properties ?? {};
        properties.table = this;

        const
            row = new tableRowClass(properties);

        row.suspendLayout.set(true);

        row.append(
            this.columnsGet()
                .map(item =>
                {
                    const
                        tableCellClass = item.tableCellClass.get();

                    Assert.isNotNullOrUndefined(tableCellClass, "tableCellClass");

                    return new tableCellClass({
                        table: this,
                        tableColumn: item,
                        tableRow: row
                    });
                }
                )
        );

        row.suspendLayout.set(false);

        return row;
    }
    public rowsGet(): JNative<HTMLElement> | TableRow[]
    {
        if (this.lightweight.get())
        {
            const
                $$element = this.$$element.get();

            Assert.isNotNullOrUndefined($$element, "$$element");

            return $$element.find("tbody tr");
        }

        Assert.isNotNullOrUndefined(this.tableBody, "this.tableBody");

        return this.tableBody.controlsGet() as TableRow[];
    }

    // #endregion

    // #region Protected Methods

    protected _asyncRefreshFromDataSourceChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _footerVisibleChanged(change: PropertyChange<boolean>)
    {
        Assert.isNotNullOrUndefined(this.tableFoot, "this.tableFoot");

        this.tableFoot.visible.set(change.currentValue);
    }
    protected _headerVisibleChanged(change: PropertyChange<boolean>)
    {
        Assert.isNotNullOrUndefined(this.tableHead, "this.tableHead");

        this.tableHead.visible.set(change.currentValue);
    }
    protected _ledgerChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-feature-ledger");
        else
            this.removeCssClass("rad-feature-ledger");
    }
    protected _lightweightChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _linkUrlsChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _messageWhenNoRowsChanged(change: PropertyChange<string>)
    {
        /* Intentionally empty */
    }
    protected _noResultsRowAdd()
    {
        const
            messageWhenNoRows = this.messageWhenNoRows.get();

        if (!messageWhenNoRows)
            return;

        if (!this._tableRowNoResults)
        {
            this._tableRowNoResults = new TableRowNoResults({ table: this });

            const
                columns = this.columnsGet();

            this._tableRowNoResults.suspendLayout.set(true);

            const
                cell = new TableCell({
                    columnSpan: columns.length,
                    dataSource: new DataSource({ data: { text: messageWhenNoRows } }),
                    dataSourceProperty: "text",
                    tableRow: this._tableRowNoResults
                });

            this._tableRowNoResults.append(cell);

            this._tableRowNoResults.suspendLayout.set(false);
        }

        Assert.isNotNullOrUndefined(this.tableBody, "this.tableBody");

        this.tableBody.append(this._tableRowNoResults);
    }
    protected _noResultsRowRemove()
    {
        this._tableRowNoResults?.detach();
    }
    protected _rowClickableChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-feature-clickable");
        else
            this.removeCssClass("rad-feature-clickable");
    }
    public _rowClicked(row: TableRow)
    {
        const
            rowSelectType = this.rowSelectType.get();

        if (this.rowClickable.get())
        {
            this.triggerHandler("rowClicked", row);
            return;
        }

        if (rowSelectType === TableRowSelectTypeEnum.single || rowSelectType === TableRowSelectTypeEnum.multiple)
        {
            const
                tableCellSelection = ArrayHelper.firstOrDefault(row.controlsGet(), item => item instanceof TableCellSelection) as Nullable<TableCellSelection>;

            if (tableCellSelection && tableCellSelection.checkBox)
                tableCellSelection.checkBox.value.set(!tableCellSelection.checkBox.value.get())

            return;
        }

        if (rowSelectType === TableRowSelectTypeEnum.singleNoCheckBox)
            this._setRowSelectionState(row, true);
    }
    protected _rowIconChanged(change: PropertyChange<string>)
    {
        this._updateColumns();
    }
    protected _rowIconDataSourcePathChanged(change: PropertyChange<string>)
    {
        this._updateColumns();
    }
    protected _rowIconMobileChanged(change: PropertyChange<string>)
    {
        this._updateColumns();
    }
    protected _rowSelectTypeChanged(change: PropertyChange<string>)
    {
        this._updateColumns();
    }
    protected _selectedItemsChanged(change: PropertyChange<any[]>)
    {
        this.triggerHandler("selectedItemsChanged", change);
    }
    public _selectedItemsUpdateResume()
    {
        this.__selectedItemsUpdateSuspended = false;

        if (this.lightweight.get())
        {
            const
                selectedRows: HTMLTableRowElement[] = [];

            (this.rowsGet() as JNative<HTMLTableRowElement>)
                .each(function (index, element)
                {
                    const
                        $$this = $$(element);

                    if ($$this.hasClass("rad-state-selected"))
                        selectedRows.push(element);
                });

            this._updateSelectedItems($$(selectedRows));
        }
        else
        {
            this._updateSelectedItems(
                (this.rowsGet() as TableRow[])
                    .filter(item => item._selectedGet())
            );
        }
    }
    public _selectedItemsUpdateSuspend()
    {
        this.__selectedItemsUpdateSuspended = true;
    }
    public _setRowSelectionState(row: JNative<HTMLTableRowElement> | TableRow, selected: boolean)
    {
        if (this.__settingRowSelectionState)
            return;

        const
            rowIsSelected =
                this.lightweight.get()
                    ? (row as JNative<HTMLTableRowElement>).hasClass("rad-state-selected")
                    : (row as TableRow).hasCssClass("rad-state-selected");

        if (rowIsSelected === selected)
            return;

        this.__settingRowSelectionState = true;

        const
            rowSelectType = this.rowSelectType.get();

        if (this.lightweight.get())
        {
            if (selected)
            {
                (row as JNative<HTMLTableRowElement>).addClass("rad-state-selected");

                if (rowSelectType === TableRowSelectTypeEnum.single || rowSelectType === TableRowSelectTypeEnum.singleNoCheckBox)
                {
                    (this.rowsGet() as JNative<HTMLTableRowElement>)
                        .each((index, element) =>
                        {
                            $$(element).removeClass("rad-state-selected");
                        });
                }

                (row as JNative<HTMLTableRowElement>).addClass("rad-state-selected");
            }
            else
                (row as JNative<HTMLTableRowElement>).removeClass("rad-state-selected");

            if (!this.__selectedItemsUpdateSuspended)
            {
                const
                    selectedRows: HTMLTableRowElement[] = [];

                (this.rowsGet() as JNative<HTMLTableRowElement>)
                    .each((index, element) =>
                    {
                        const
                            $$this = $$(element);

                        if ($$this.hasClass("rad-state-selected"))
                            selectedRows.push(element);
                    });

                this._updateSelectedItems(selectedRows);
            }
        }
        else
        {
            if (selected)
            {
                (row as TableRow).select();

                if (rowSelectType === TableRowSelectTypeEnum.single || rowSelectType === TableRowSelectTypeEnum.singleNoCheckBox)
                {
                    (this.rowsGet() as TableRow[])
                        .filter(item => item !== row)
                        .forEach(item => { item.deselect(); });
                }
            }
            else
                (row as TableRow).deselect();

            if (!this.__selectedItemsUpdateSuspended)
            {
                this._updateSelectedItems(
                    (this.rowsGet() as TableRow[])
                        .filter(item => item._selectedGet())
                );
            }
        }

        this.triggerHandler("rowSelectionStateChanged", {
            originalValue: !selected,
            currentValue: selected,
            row: row
        });

        this.__settingRowSelectionState = false;
    }
    protected _stackableChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this._tableElementRef.current.removeClass("unstackable");
        else
            this._tableElementRef.current.addClass("unstackable");
    }
    protected _tableRowClassChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    public _triggerRowDataSourceChanged(tableRow: TableRow, change: any)
    {
        this.trigger("rowDataSourceChanged", {
            currentValue: change.currentValue,
            dataSource: change.dataSource,
            originalValue: change.originalValue,
            path: change.path,
            tableRow: tableRow
        });
    }
    protected _updateCellColumnDescriptions()
    {
        const
            columns = this.columnsGet(),
            tableRows = this.tableBody?.controlsGet() as TableRow[] | undefined;

        if (tableRows)
        {
            for (let i = 0; i < tableRows.length; i++)
            {
                const
                    tableCells = tableRows[i].controlsGet();

                for (let j = 0; j < columns.length; j++)
                {
                    if (tableCells.length > j)
                        tableCells[j].columnDescription.set(columns[j].text.get());
                    else
                        break;
                }
            }
        }
    }
    protected _updateColumns()
    {
        const
            rowSelectType = this.rowSelectType.get();

        let
            columnsChanged = false;

        if (rowSelectType && rowSelectType !== TableRowSelectTypeEnum.none)
            this.addCssClass("rad-feature-selectable");
        else
            this.removeCssClass("rad-feature-selectable");

        if (rowSelectType === TableRowSelectTypeEnum.single || rowSelectType === TableRowSelectTypeEnum.multiple)
        {
            if (!this.selectionColumn)
            {
                this.selectionColumn =
                    new TableColumnSelection({
                        rowSelectType: this.rowSelectType.get(),
                        table: this
                    });
                columnsChanged = true;
            }

            this.selectionColumn.rowSelectType.set(this.rowSelectType.get());
        }
        else
        {
            if (this.selectionColumn)
            {
                this.selectionColumn.remove();
                delete this.selectionColumn;
                columnsChanged = true;
            }
        }

        const
            rowIcon = this.rowIcon.get(),
            rowIconDataSourcePath = this.rowIconDataSourcePath.get(),
            rowIconMobile = this.rowIconMobile.get();

        if ((rowIcon || rowIconDataSourcePath || rowIconMobile) && !this.iconColumn)
        {
            this.iconColumn =
                new TableColumnIcon({
                    table: this
                });
            columnsChanged = true;
        }
        else if (this.iconColumn)
        {
            this.iconColumn.remove();
            delete this.iconColumn;
            columnsChanged = true;
        }

        if (columnsChanged)
            this.clear();

        if (this.iconColumn)
            this.iconColumn.prependTo(this.tableHead);

        if (this.selectionColumn)
            this.selectionColumn.prependTo(this.tableHead);
    }
    protected _updateSelectedItems(selectedItems: (HTMLTableRowElement | TableRow)[])
    {
        if (ArrayHelper.sequenceEqualTo(this.selectedItems.get() ?? [], selectedItems))
            return;

        if (this.lightweight.get())
            this.selectedItems.set($$(selectedItems as HTMLTableRowElement[]));
        else
            this.selectedItems.set(selectedItems as TableRow[]);
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: TablePropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this._tableElementRef = new ElementRef();
        this._tableBodyControlRef = new ControlRef();
        this._tableFootControlRef = new ControlRef();
        this._tableHeadControlRef = new ControlRef();

        this.asyncRefreshFromDataSource = new PropertyBoolean(this, propertyValues.asyncRefreshFromDataSource, this._asyncRefreshFromDataSourceChanged);
        this.footerVisible = new PropertyBoolean(this, propertyValues.footerVisible, this._footerVisibleChanged);
        this.headerVisible = new PropertyBoolean(this, propertyValues.headerVisible, this._headerVisibleChanged);
        this.ledger = new PropertyBoolean(this, propertyValues.ledger, this._ledgerChanged);
        this.lightweight = new PropertyBoolean(this, propertyValues.lightweight, this._lightweightChanged);
        this.linkUrls = new PropertyBoolean(this, propertyValues.linkUrls, this._linkUrlsChanged);
        this.messageWhenNoRows = new PropertyString(this, propertyValues.messageWhenNoRows, this._messageWhenNoRowsChanged);
        this.rowIcon = new PropertyString({
            context: this,
            initialValue: propertyValues.rowIcon,
            changeHandler: this._rowIconChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Row Icon" }),
            ],
        });
        this.rowIconDataSourcePath = new PropertyString(this, propertyValues.rowIconDataSourcePath, this._rowIconDataSourcePathChanged);
        this.rowIconMobile = new PropertyString({
            context: this,
            initialValue: propertyValues.rowIconMobile,
            changeHandler: this._rowIconMobileChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Row Icon (Mobile)" }),
            ],
        });
        this.rowClickable = new PropertyBoolean({
            context: this,
            initialValue: propertyValues.rowClickable,
            changeHandler: this._rowClickableChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Behavior" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Row Clickable" }),
            ],
        });
        this.rowSelectType = new Property<TableRowSelectTypeEnum>({
            context: this,
            dotNetType: "System.String, System.Private.CoreLib",
            initialValue: propertyValues.rowSelectType,
            changeHandler: this._rowSelectTypeChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Behavior" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Row Select Type" }),
            ],
        });
        this.selectedItems = new Property<any[]>(this, "System.Object, System.Private.CoreLib", propertyValues.selectedItems, this._selectedItemsChanged);
        this.stackable = new PropertyBoolean(this, propertyValues.stackable, this._stackableChanged);
        this.tableRowClass = new PropertyObject(this, propertyValues.tableRowClass, this._tableRowClassChanged);
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement();

        this.tableHead = this._tableHeadControlRef.current;
        this.tableBody = this._tableBodyControlRef.current;
        this.tableFoot = this._tableFootControlRef.current;

        return $$element;
    }
    protected override _createElementHtml(children?: JsxFragment | string | (JsxFragment | string | undefined)[])
    {
        return (
            <div>
                <table ref={this._tableElementRef} class="ui celled table">
                    <TableHead ref={this._tableHeadControlRef} />
                    <TableBody ref={this._tableBodyControlRef} />
                    <TableFoot ref={this._tableFootControlRef} />
                </table>
            </div>
        );
    }
    protected override _refreshFromDataSource()
    {
        super._refreshFromDataSource();

        if (this.selectionColumn)
            this.selectionColumn.checkBox?.value.set(false);

        const
            dataSource = this.dataSource.get();

        if (!dataSource)
            return;

        const
            data = dataSource.data.get();

        if (!data)
            return;

        if (!data.length)
        {
            this.addCssClass("rad-state-no-results");
            this.clear();
            this._noResultsRowAdd();
            return;
        }

        this.removeCssClass("rad-state-no-results");
        this._noResultsRowRemove();

        const
            lightweight = this.lightweight.get();

        let
            columns: TableColumnBase[],
            tableCellClasses: Nullable<Constructor<TableCellBase>>[];

        if (lightweight)
        {
            columns = this.columnsGet();
            tableCellClasses = columns.map(item => item.tableCellClass.get());
        }

        function createRow(table: Table, item: any): JNative<HTMLTableRowElement> | TableRow
        {
            const
                tableRow = table.newRow(),
                rowDataSource = new DataSource({ data: item });

            (rowDataSource as any).tableRow = tableRow;

            rowDataSource.on("propertyValueChanged", { self: table }, table.onRowDataSourcePropertyValueChanged);

            if (lightweight)
            {
                const
                    $$tds = (tableRow as JNative<HTMLTableRowElement>).find("td");

                (tableRow as JNative<HTMLTableRowElement>).data("dataSource", rowDataSource);

                columns
                    .forEach((column, index) =>
                    {
                        const
                            tableCellClass = tableCellClasses[index];

                        if ((tableCellClass as any).createLightweightContent)
                        {
                            if ($$tds.length > index)
                                (tableCellClass as any).createLightweightContent($$($$tds[index]), column, rowDataSource);
                        }
                    });
            }
            else
                (tableRow as TableRow).dataSource.set(rowDataSource);

            return tableRow;
        }

        if (this.asyncRefreshFromDataSource.get())
        {
            if (!this.asyncIterator)
                this.asyncIterator = new AsyncIterator();

            this._loadingEnqueue();

            this.asyncIterator
                .forEach(data, (item: any) => createRow(this, item))
                .then((results: JNative<HTMLTableRowElement> | TableRow[]) => { this.clear(); this.addRows(results); })
                .catch(() => { /* Intentionally empty */ })
                .finally(() => { this._loadingDequeue(); });
        }
        else if (!lightweight)
        {
            const
                rows = this.rowsGet() as TableRow[];

            if (rows.length > data.length)
            {
                for (let i = data.length; i < rows.length; i++)
                    rows[i].remove();
            }

            for (let i = 0; i < rows.length && i < data.length; i++)
                rows[i].dataSource.set(new DataSource({ data: data[i] }));

            if (data.length > rows.length)
            {
                const
                    newRows: TableRow[] = [];

                for (let i = rows.length; i < data.length; i++)
                    newRows.push(createRow(this, data[i]) as TableRow);

                this.addRows(newRows);
            }
        }
        else
        {
            this.clear();
            this.addRows(data.map((item: any) => createRow(this, item)));
        }
    }

    public override remove()
    {
        this.removeControls([
            this._tableRowNoResults,
            this.tableHead,
            this.tableBody,
            this.tableFoot,
            this.iconColumn,
            this.selectionColumn,
        ]);

        super.remove();
    }

    // #endregion

    // #region Event Handlers

    protected onRowDataSourcePropertyValueChanged(evt: IJNativeEvent, change: DataSourcePropertyValueChange<any>)
    {
        const
            self = evt.data.self as this;

        self.triggerHandler("rowDataSourcePropertyValueChanged", {
            dataSource: change.dataSource,
            path: change.path,
            originalValue: change.originalValue,
            currentValue: change.currentValue,
            row: (change.dataSource as any).tableRow
        });
    }

    // #endregion

    // #endregion
}

// #endregion